import React, { useRef, useEffect } from "react"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"
import { Card, CardHeader, CardContent } from "@mui/material"
import dayjs from "dayjs"

export default function MarkerObservationChart({ observations, markerName, highlightedDate }) {
  // Filter for version 2 observations and transform data for charting
  const v2Observations = (observations || [])
    .filter((obs) => obs.payload_version === "2")
    .sort((a, b) => {
      const dateA = new Date(a.data?.date || a.observation_date)
      const dateB = new Date(b.data?.date || b.observation_date)
      return dateA - dateB
    })
    .map((obs) => {
      const observationDateStr = obs.data?.date || obs.observation_date
      return {
        date: dayjs(observationDateStr).format("MMM DD, HH:mm"),
        timestamp: new Date(observationDateStr).getTime(),
        fullDate: observationDateStr,
        seaweed_length: obs.data?.seaweed_length ? parseFloat(obs.data.seaweed_length) : 0,
        notes: obs.data?.notes || "",
      }
    })

  const containerRef = useRef(null)
  useEffect(() => {
    // Intentionally left blank; previously used for debugging container size and svg inspection.
    // Keep hook to preserve potential future side-effects that depend on v2Observations.
  }, [v2Observations])

  // Custom dot component that highlights based on hovered image
  const CustomDot = (props) => {
    const { cx, cy, payload } = props
    const payloadTime = new Date(payload.fullDate).getTime()
    const highlightedTime = highlightedDate ? new Date(highlightedDate).getTime() : null
    const isHighlighted = highlightedTime && payloadTime === highlightedTime

    return (
      <circle
        cx={cx}
        cy={cy}
        r={isHighlighted ? 8 : 4}
        fill={isHighlighted ? "#ff4444" : "#8884d8"}
        stroke={isHighlighted ? "#fff" : "none"}
        strokeWidth={isHighlighted ? 2 : 0}
        style={{
          transition: "all 0.2s ease",
          filter: isHighlighted ? "drop-shadow(0 0 8px rgba(255, 68, 68, 0.8))" : "none",
        }}
      />
    )
  }

  // Custom tooltip component to display notes
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          <p style={{ margin: "0 0 5px 0", fontWeight: "bold" }}>{data.date}</p>
          <p style={{ margin: "0 0 5px 0", color: "#8884d8" }}>
            Seaweed Length: {data.seaweed_length.toFixed(2)} cm
          </p>
          {data.notes && (
            <p style={{ margin: "5px 0 0 0", fontSize: "12px", fontStyle: "italic", color: "#666" }}>
              Notes: {data.notes}
            </p>
          )}
        </div>
      )
    }
    return null
  }

  if (v2Observations.length === 0) {
    return (
      <Card>
        <CardHeader title={`${markerName} - Observations`} />
        <CardContent>
          <p style={{ color: "#999" }}>No version 2 observations available</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader title={`${markerName} - Observations Chart`} />
      <CardContent>
        {/* parent div must have explicit height for ResponsiveContainer to measure */}
        <div ref={containerRef} style={{ width: "100%", height: 300, position: "relative" }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={v2Observations} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              {/* Make grid deterministic with explicit axis ids */}
              <CartesianGrid strokeDasharray="3 3" xAxisId="bottom" yAxisId="left" />
              <XAxis
                dataKey="timestamp"
                type="number"
                domain={["dataMin", "dataMax"]}
                tickFormatter={(ts) => dayjs(ts).format("MMM DD, HH:mm")}
                tick={{ fontSize: 12 }}
                xAxisId="bottom"
              />
              <YAxis id="left" yAxisId="left" domain={[0, 'auto']} label={{ value: "Seaweed Length (cm)", angle: -90, position: "insideLeft" }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line type="monotone" dataKey="seaweed_length" stroke="#ff4444" strokeWidth={3} dot={<CustomDot />} activeDot={{ r: 6 }} name="Seaweed Length (cm)" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}
